/**
 * CMS Channel Lists Query
 *
 * Server-side query for fetching channel lists from Payload CMS.
 * Used to build the main player playlist based on the inMainPlayer flag.
 */

import { query } from "@solidjs/router";
import type { CMSChannelListResponse, CMSChannelList, CMSChannel } from "~/types/cms";
import { logger } from "~/lib/logger/logger";
import { env } from "~/config/env";
import { buildMainPlayerChannelListsUrl } from "../queryUrls";
import { fetchFromCMS, filterByTenant } from "../queryUtils";
import { evictIfFull, touchEntry } from "~/lib/cache/cappedMap";

/** 60s cache — channel lists rarely change */
const CACHE_TTL_MS = 60_000;
const MAX_CACHE_SIZE = 20;
const channelListsCache = new Map<string, { data: CMSChannelList[]; expiresAt: number }>();

/** Clear the server-side channel lists Map cache. Called by webhook invalidation. */
export function clearChannelListsCache(tenantSlug?: string) {
  if (tenantSlug) {
    channelListsCache.delete(tenantSlug);
  } else {
    channelListsCache.clear();
  }
}

/**
 * Server-side query for fetching channel lists marked for main player
 *
 * @param tenantSlug - The tenant slug (e.g., 'absolut-radio')
 * @returns Array of channel lists with populated channels
 */
export const getMainPlayerChannelListsQuery = query(async (tenantSlug: string) => {
  "use server";

  const LOG_PREFIX = "[mainPlayerChannelLists]";

  const cached = channelListsCache.get(tenantSlug);
  if (cached && Date.now() < cached.expiresAt) {
    touchEntry(channelListsCache, tenantSlug);
    return cached.data;
  }

  const url = `${env.cmsApiUrl}${buildMainPlayerChannelListsUrl()}`;

  const data = await fetchFromCMS<CMSChannelListResponse>(url, { logPrefix: LOG_PREFIX });

  if (!data?.docs || !Array.isArray(data.docs)) {
    logger.warn(`${LOG_PREFIX} Invalid response structure`);
    return [];
  }

  logger.info(`${LOG_PREFIX} Received ${data.docs.length} channel lists`);

  // Filter by tenant (Payload doesn't support querying nested relationship fields)
  const channelLists = filterByTenant(data.docs, tenantSlug);

  if (channelLists.length === 0) {
    logger.warn(`${LOG_PREFIX} No channel lists found for tenant: ${tenantSlug}`);
  } else {
    logger.info(`${LOG_PREFIX} Found ${channelLists.length} channel lists`);
  }

  evictIfFull(channelListsCache, MAX_CACHE_SIZE);
  channelListsCache.set(tenantSlug, { data: channelLists, expiresAt: Date.now() + CACHE_TTL_MS });
  return channelLists;
}, "mainPlayerChannelLists");

/**
 * Extract unique channels from channel lists
 * Flattens channel lists and removes duplicates
 *
 * @param channelLists - Array of channel lists with populated channels
 * @returns Array of unique channels
 */
export function extractChannelsFromLists(
  channelLists: CMSChannelListResponse["docs"]
): CMSChannel[] {
  const channelMap = new Map<string, CMSChannel>();

  channelLists.forEach((list) => {
    if (!list.channels) return;

    list.channels.forEach((channel) => {
      // Skip if channel is not populated (just an ID string/number)
      if (typeof channel === "string" || typeof channel === "number") {
        logger.warn(`Channel not populated in list ${list.name}: ${channel}`);
        return;
      }

      // Add channel to map (using ID as key to prevent duplicates)
      if (!channelMap.has(channel.id)) {
        channelMap.set(channel.id, channel);
      }
    });
  });

  const channels = Array.from(channelMap.values());
  logger.api(`Extracted ${channels.length} unique channels from lists`);

  return channels;
}
